<template>
  <v-dialog
    :value="value"
    persistent
    max-width="700"
    @input="$emit('input', $event)"
  >
    <v-card :loading="loading">
      <v-card-title>
        <v-icon class="mr-2" :color="getColor(group.label)">
          {{ group.icon || 'mdi-folder-outline' }}
        </v-icon>
        <span class="text-h6">{{ group.label }}</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-subtitle class="pb-0">
        Select the licences that belong to this group
      </v-card-subtitle>

      <v-card-text class="pt-3">
        <v-text-field
          v-model="searchText"
          dense
          outlined
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          label="Search licences..."
          class="mb-3"
        ></v-text-field>

        <v-list
          dense
          class="assign-licence-list"
          style="max-height: 400px; overflow-y: auto"
        >
          <v-list-item-group v-model="selectedIds" multiple>
            <v-list-item
              v-for="licence in filteredLicences"
              :key="licence.id"
              :value="licence.id"
              :disabled="licence.is_parent || licence.is_deleted"
            >
              <template v-slot:default="{ active }">
                <v-list-item-action>
                  <v-checkbox
                    :input-value="active"
                    color="primary"
                    :disabled="licence.is_parent || licence.is_deleted"
                  ></v-checkbox>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>
                    <code class="font-weight-bold text-uppercase">{{
                      licence.licence_name
                    }}</code>
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <span
                      v-if="licence.is_deleted"
                      class="red--text text--darken-2"
                    >
                      <v-icon x-small color="red darken-2">mdi-delete</v-icon>
                      Deleted
                    </span>
                    <span
                      v-else-if="
                        licence.current_group &&
                        licence.current_group !== group.label
                      "
                      class="orange--text text--darken-2"
                    >
                      <v-icon x-small color="orange darken-2">mdi-alert</v-icon>
                      Currently in: {{ licence.current_group }}
                    </span>
                    <span v-else-if="licence.is_parent" class="grey--text">
                      (This is a parent group)
                    </span>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip
                    x-small
                    :color="
                      licence.is_active ? 'green lighten-4' : 'red lighten-4'
                    "
                  >
                    {{ licence.is_active ? 'Active' : 'Passive' }}
                  </v-chip>
                </v-list-item-action>
              </template>
            </v-list-item>
          </v-list-item-group>

          <div
            v-if="filteredLicences.length === 0"
            class="text-center py-4 grey--text"
          >
            No licences found
          </div>
        </v-list>

        <div class="mt-2 text-caption grey--text">
          {{ selectedIds.length }} licence(s) selected
        </div>
      </v-card-text>

      <v-card-actions>
        <v-btn text small @click="selectAll" color="primary">
          <v-icon small left>mdi-check-all</v-icon>Select All
        </v-btn>
        <v-btn text small @click="clearSelection">
          <v-icon small left>mdi-close</v-icon>Clear
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn color="primary" :loading="saving" @click="save">
          <v-icon left>mdi-content-save</v-icon>
          Save Assignment
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Swal from 'sweetalert2'
import { apiService } from '../../service/api.service'

export default {
  name: 'AssignLicencesDialog',

  props: {
    value: {
      type: Boolean,
      default: false
    },
    group: {
      type: Object,
      default: () => ({})
    },
    allGroups: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      loading: false,
      saving: false,
      searchText: '',
      allLicences: [],
      selectedIds: []
    }
  },

  computed: {
    filteredLicences () {
      if (!this.searchText) return this.allLicences
      const q = this.searchText.toLowerCase()
      return this.allLicences.filter((l) =>
        l.licence_name.toLowerCase().includes(q)
      )
    }
  },

  watch: {
    value (open) {
      if (open) {
        this.loadLicences()
      } else {
        this.reset()
      }
    }
  },

  methods: {
    getColor (label) {
      const colors = {
        'AI Platform': 'blue darken-1',
        'Application Hub': 'deep-purple darken-1'
      }
      return colors[label] || 'teal darken-1'
    },

    async loadLicences () {
      this.searchText = ''
      this.loading = true

      try {
        const { data } = await apiService.get('admin-get-licences')

        // Build a map: licence_id → parent group label
        const parentMap = {}
        for (const g of this.allGroups) {
          for (const child of g.children || []) {
            parentMap[child.id] = g.label
          }
        }

        // All items from admin-get-licences API are actual licences (different table than parent groups)
        this.allLicences = (data.data || []).map((l) => ({
          ...l,
          is_parent: false,
          is_deleted: l.deleted || false,
          current_group: parentMap[l.id] || null
        }))

        // Pre-select current children
        const currentChildren = (this.group.children || []).map((c) => c.id)
        this.selectedIds = currentChildren
      } catch (err) {
        console.error('Error loading licences for assign:', err)
      } finally {
        this.loading = false
      }
    },

    reset () {
      this.allLicences = []
      this.selectedIds = []
      this.searchText = ''
    },

    close () {
      this.$emit('input', false)
    },

    selectAll () {
      this.selectedIds = this.allLicences
        .filter((l) => !l.is_parent && !l.is_deleted)
        .map((l) => l.id)
    },

    clearSelection () {
      this.selectedIds = []
    },

    async save () {
      this.saving = true
      try {
        await apiService.post(
          `admin-assign-licences-to-group/${this.group.id}`,
          { licence_ids: this.selectedIds }
        )
        Swal.fire(
          'Saved!',
          `Licences assigned to "${this.group.label}" successfully.`,
          'success'
        )
        this.close()
        this.$emit('saved')
      } catch (err) {
        const msg = err.response?.data?.message || 'An error occurred'
        Swal.fire('Error!', msg, 'error')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
